import { Button, Modal, ModalBaseProps, ModalTitle } from '@dashboard/ui'
import { useTranslation } from 'react-i18next'
import { useEffect, useState } from 'react'
import { dashboardClient, getErrorText } from '@/api'
import { useParams } from 'react-router-dom'
import { useCurrentGame } from '@/api/useCompaniesQuery'
import { PayoutRequestPreview } from '@/api/dashboard'
import { formatMoney } from '@/util'
import AnimatedDots from '@/components/animated-dots/AnimatedDots'
import { ToastSeverity, useToast } from '@/ui'

interface InstantPayoutModalProps extends ModalBaseProps {
  onCreated: (total: number) => void
}

export const InstantPayoutModal = ({ ...rest }: InstantPayoutModalProps) => {
  const { t } = useTranslation()
  const { companyId, gameId } = useParams() as { companyId: string; gameId: string }
  const { game } = useCurrentGame()
  const showToast = useToast()
  const [payoutRequestPreview, setPayoutRequestPreview] = useState<PayoutRequestPreview | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const loadPreview = async () => {
    const { data } = await dashboardClient.v1.getPayoutRequestPreview({
      companyId,
      gameId,
      is_sandbox: !!game?.sandbox,
    })

    setPayoutRequestPreview(data)
  }

  useEffect(() => {
    loadPreview()
  }, [companyId, gameId])

  const confirmRequest = async () => {
    setIsLoading(true)
    try {
      await dashboardClient.v1.createPayout({
        companyId,
        gameId,
        is_sandbox: !!game?.sandbox,
      })
      rest.onCreated(payoutRequestPreview?.total || 0)
      rest.onClose?.()
    } catch (e) {
      showToast({ message: getErrorText(e), severity: ToastSeverity.error })
    } finally {
      setIsLoading(false)
    }
  }

  const renderRow = (text: string, value?: number) => {
    return (
      <div className="flex justify-between">
        <div>{text}</div>
        <div>{payoutRequestPreview && value != undefined ? formatMoney(value) + ' USD' : <AnimatedDots />}</div>
      </div>
    )
  }

  const renderTable = () => {
    const total = payoutRequestPreview && payoutRequestPreview.total > 0 ? payoutRequestPreview.total : 0

    const pendingAndReserves = ((payoutRequestPreview?.pending_payments_reserves || 0) / 100)
      .toLocaleString('en-US', {
        maximumFractionDigits: 2,
      })
      .replace(/,/g, ',')

    return (
      <div
        className="bg-fg-primary p-3 font-[Menlo] text-caption-md text-text-tertiary"
        style={{
          boxShadow: '0px 22.336px 17.869px 0px rgba(43, 55, 78, 0.04) inset',
        }}
      >
        <div className="mb-2 text-center">
          <div>{t('payout.amount')}</div>
          <div className="text-lg font-bold">
            {payoutRequestPreview ? formatMoney(total) + ' USD' : <AnimatedDots />}
          </div>
        </div>

        <div className="flex text-caption-sm uppercase">{t('payout.overview')}</div>
        <div className="mb-2 mt-1 border-t border-dashed border-border-secondary" />

        {renderRow(t('payout.current-balance'), payoutRequestPreview?.balance)}
        <div className="my-2 border-t border-dashed border-border-secondary" />

        <div className="flex justify-between">
          <div>{t('payout.reserves')}</div>
          <div>{payoutRequestPreview ? `-$(${pendingAndReserves} USD)` : <AnimatedDots />}</div>
        </div>

        <div className="my-2 border-t border-dashed border-border-secondary" />

        {renderRow(t('payout.transfer-fee'), payoutRequestPreview ? -payoutRequestPreview?.transfer_fee : undefined)}
        <div className="my-2 border-t border-dashed border-border-secondary" />

        <div className="flex justify-between text-caption-lg font-semibold">
          <div>{t('payout.total')}</div>
          <div>{payoutRequestPreview ? formatMoney(total) : <AnimatedDots />}</div>
        </div>
      </div>
    )
  }

  return (
    <Modal {...rest} size="md">
      <div className="px-6">
        <ModalTitle className="pb-0" titleClassName="text-center w-full">
          {t('payouts.instant.modal.title')}
        </ModalTitle>

        <div
          className="overflow-hidden rounded-md border border-border-secondary bg-fg-secondary"
          style={{
            boxShadow:
              '4px 0px 18px 0px rgba(100, 116, 139, 0.04) inset, -4px 0px 18px 0px rgba(100, 116, 139, 0.04) inset;',
          }}
        >
          {renderTable()}

          <div className="font-[Menlo] text-text-tertiary">
            <svg width="432" height="8" viewBox="0 0 432 8" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M1 -0.499988L0.5 -0.499988L0.5 1.17899e-05L0.5 2.43822C0.5 5.2233 3.59499 6.89273 5.92237 5.36303L10.1708 2.57068C10.9283 2.07285 11.8958 2.02397 12.6995 2.44293L21.119 6.83167C22.1328 7.36013 23.3408 7.36013 24.3547 6.83167L32.9497 2.35143C33.6738 1.97396 34.5367 1.97396 35.2608 2.35143L43.8559 6.83167C44.8697 7.36013 46.0777 7.36013 47.0915 6.83167L55.6865 2.35143C56.4107 1.97396 57.2735 1.97396 57.9977 2.35143L66.5927 6.83167C67.6065 7.36013 68.8145 7.36013 69.8283 6.83167L78.4234 2.35143C79.1475 1.97396 80.0104 1.97396 80.7345 2.35143L89.3296 6.83168C90.3434 7.36013 91.5514 7.36013 92.5652 6.83168L101.16 2.35143C101.884 1.97396 102.747 1.97396 103.471 2.35143L112.066 6.83167C113.08 7.36013 114.288 7.36013 115.302 6.83167L123.897 2.35143C124.621 1.97396 125.484 1.97396 126.208 2.35143L134.803 6.83168C135.817 7.36013 137.025 7.36013 138.039 6.83168L146.634 2.35143C147.358 1.97396 148.221 1.97396 148.945 2.35143L149.176 1.90805L148.945 2.35143L157.54 6.83168C158.554 7.36013 159.762 7.36013 160.776 6.83168L169.371 2.35143C170.095 1.97396 170.958 1.97396 171.682 2.35143L171.913 1.90893L171.682 2.35143L180.277 6.83167C181.291 7.36014 182.499 7.36013 183.513 6.83168L192.108 2.35143C192.832 1.97396 193.695 1.97396 194.419 2.35143L203.014 6.83168C204.028 7.36014 205.236 7.36013 206.249 6.83168L214.844 2.35143L214.613 1.90805L214.844 2.35143C215.569 1.97396 216.431 1.97396 217.156 2.35143L225.751 6.83168C226.764 7.36013 227.972 7.36013 228.986 6.83168L237.581 2.35143L237.35 1.90805L237.581 2.35143C238.305 1.97396 239.168 1.97396 239.892 2.35143L248.487 6.83168C249.501 7.36013 250.709 7.36013 251.723 6.83168L260.318 2.35143C261.042 1.97396 261.905 1.97396 262.629 2.35143L271.224 6.83168C272.238 7.36014 273.446 7.36014 274.46 6.83168L283.055 2.35143C283.779 1.97396 284.642 1.97396 285.366 2.35143L293.961 6.83168C294.975 7.36014 296.183 7.36014 297.197 6.83168L305.792 2.35143C306.516 1.97397 307.379 1.97397 308.103 2.35143L316.698 6.83168C317.712 7.36014 318.92 7.36014 319.934 6.83168L328.529 2.35143C329.253 1.97396 330.116 1.97397 330.84 2.35143L339.435 6.83168C340.449 7.36014 341.657 7.36014 342.67 6.83168L351.265 2.35144C351.99 1.97397 352.852 1.97397 353.577 2.35143L362.172 6.83168C363.185 7.36014 364.393 7.36014 365.407 6.83168L374.002 2.35143C374.726 1.97397 375.589 1.97397 376.313 2.35143L384.909 6.83168C385.922 7.36014 387.13 7.36014 388.144 6.83168L396.739 2.35144C397.463 1.97396 398.326 1.97397 399.05 2.35143L407.645 6.83168C408.659 7.36014 409.867 7.36014 410.881 6.83168L419.3 2.44294C420.104 2.02398 421.072 2.07286 421.829 2.57068L426.078 5.36303C428.405 6.89273 431.5 5.2233 431.5 2.43823L431.5 1.88177e-05L431.5 -0.499981L431 -0.499981L1 -0.499988Z"
                fill="#fff"
                stroke="#F1F5F9"
              />
            </svg>
            <div className="m-3 text-caption-sm">{t('payouts.instant.modal.text1')}</div>
            <div className="m-3 text-caption-sm">{t('payouts.instant.modal.text2')}</div>
            <div className="m-3 text-caption-sm">{t('payouts.instant.modal.text3')}</div>
          </div>
        </div>
      </div>
      <div className="mt-[18px] flex items-center justify-end gap-3 px-6 pb-6">
        <Button variant="outline" color="secondary" size="sm" onClick={rest.onClose}>
          {t('Cancel')}
        </Button>
        <Button
          size="sm"
          onClick={confirmRequest}
          loading={isLoading}
          disabled={!payoutRequestPreview || payoutRequestPreview.total < 100}
        >
          {t('payouts.confirm')}
        </Button>
      </div>
    </Modal>
  )
}
